/** @format */

import * as R from 'ramda';

import {round} from 'helpers/numberHelpers';
import {YalperRegion} from 'graphql/generated/types';

export const REGION_SENSORS: string[] = ['as-geo'];

export interface ChecksWithSensor {
  checks: number;
  cost: number;
  found: number;
  new: number;
  sensor: {
    id: string;
    name: string;
    isPaused?: boolean;
    isError: boolean;
  };
}

export interface Sensor {
  sensor: {
    id: string;
    name: string;
  };
}

interface SensorExclusionsName {
  sensorName: string;
}

export interface ReturnGroupedChecks<Check extends Sensor> {
  validChecks: Check[];
  excludedChecks: Check[];
  needRegionsChecks: Check[];
}

const compareChecks = (check1: Sensor, check2: Sensor): boolean => {
  return check1.sensor.id === check2.sensor.id;
};

export interface GroupedChecksOptions<
  Check extends Sensor,
  Exclusion extends SensorExclusionsName,
> {
  allChecks: ReadonlyArray<Check>;
  sensorExclusions: ReadonlyArray<Exclusion>;
  regions: ReadonlyArray<YalperRegion>;
}

export const groupedChecks = <Check extends Sensor, Exclusion extends SensorExclusionsName>({
  allChecks,
  sensorExclusions,
  regions,
}: GroupedChecksOptions<Check, Exclusion>): ReturnGroupedChecks<Check> => {
  const excludedChecks = allChecks.filter(check =>
    sensorExclusions.some(v => v.sensorName === check.sensor.name),
  );

  const includedChecks = R.differenceWith(compareChecks, allChecks, excludedChecks);

  const needRegionsChecks =
    regions.length > 0 ? [] : includedChecks.filter(v => REGION_SENSORS.includes(v.sensor.name));

  const validChecks = R.differenceWith(compareChecks, includedChecks, needRegionsChecks);

  return {validChecks, excludedChecks, needRegionsChecks};
};

export const detailsPageLink = (name: string, humanName: string): string => {
  return `/home/main/details?name=${name}&humanName=${humanName}`;
};

export const groupChecksByName = (checks: ReadonlyArray<ChecksWithSensor>): ChecksWithSensor[] => {
  const grouped: Record<string, ChecksWithSensor[]> = {};

  checks.forEach(check => {
    const key = check.sensor.name;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(check);
  });

  return Object.values(grouped).map(vs => sumChecks(vs));
};

export const sumChecks = (checks: ChecksWithSensor[]): ChecksWithSensor => {
  const totalCheck = checks.reduce(
    (acc, v) => {
      acc.checks += v.checks;
      acc.cost += v.cost;
      acc.found += v.found;
      acc.new += v.new;

      return acc;
    },
    {
      checks: 0,
      cost: 0,
      found: 0,
      new: 0,
      sensor: {
        id: '',
        name: '',
        isPaused: false,
        isError: false,
      },
    },
  );

  totalCheck.cost = round(totalCheck.cost, 2);

  // Don't set platform - we usually sum checks of different platforms
  totalCheck.sensor = {
    id: checks[0].sensor.id,
    name: checks[0].sensor.name,
    isPaused: checks[0].sensor.isPaused ?? false,
    isError: checks[0].sensor.isError,
  };

  return totalCheck;
};

export const getChecksNames = <C extends Sensor>(checks: C[]): string[] =>
  checks.map(v => v.sensor.name);

export const filterSensorGroups = (
  validChecks: Sensor[],
  sensorGroup: ReadonlyArray<string>,
): string[] => {
  const allNamesSet = new Set(validChecks.map(check => check.sensor.name));
  return sensorGroup.filter(name => allNamesSet.has(name));
};
